// Dashboard Calories — saisie des prises et suivi vs seuil (adhérence, déficit, perte théorique).
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import { type FormEvent, useMemo, useState } from 'react';
import {
  BucketedCalorieChart,
  CalendarHeatmap,
  CalorieChart,
  MealBarChart,
} from '@/components/charts';
import { FoodPicker } from '@/components/FoodPicker';
import {
  ChartCard,
  EmptyState,
  ErrorBanner,
  GranularityPicker,
  Kpi,
  PeriodPicker,
  PhaseBadge,
} from '@/components/ui';
import {
  adherence,
  aggregateBy,
  caloriesByGranularity,
  consumptionNutrients,
  currentTdee,
  dailyCalories,
  dayKey,
  energyBalance,
  filterByPeriod,
  frequentFoods,
  type Granularity,
  MEAL_LABELS,
  MEAL_ORDER,
  type Meal,
  movingAverage,
  nowLocalInputValue,
  type Period,
  round,
  type TrainingPhase,
  toCalendarMatrix,
  todayKey,
} from '@/domain';
import {
  useConsumptions,
  useCreateConsumption,
  useDeleteConsumption,
  useProfile,
  useUpdateConsumption,
  useWeights,
} from '@/hooks/queries';
import { useSubmit } from '@/hooks/useSubmit';
import { confirmDelete } from '@/lib/confirm';
import { useTheme } from '@/theme/ThemeContext';

// Libellés dépendants de la phase (vert = conforme à l'objectif de la phase).
const ADHERENCE_LABEL: Record<TrainingPhase, string> = {
  cut: 'Adhérence au seuil',
  bulk: 'Jours au-dessus du seuil',
  recovery: 'Jours dans la cible',
};
const CHART_LEGEND: Record<TrainingPhase, string> = {
  cut: 'barres = apport (vert sous le seuil, rouge au-dessus)',
  bulk: 'barres = apport (vert au-dessus du surplus, rouge en dessous)',
  recovery: 'barres = apport (vert dans la bande ±10 %, orange hors cible)',
};
const HEATMAP_LEGEND: Record<TrainingPhase, string> = {
  cut: 'vert = sous le seuil, rouge = au-dessus',
  bulk: 'vert = au-dessus du surplus, rouge = en dessous',
  recovery: 'vert = dans la bande ±10 %, orange = hors cible',
};

export function CaloriesPage() {
  const { data: profile } = useProfile();
  const { data: consumptions = [] } = useConsumptions();
  const { data: weights = [] } = useWeights();
  const createC = useCreateConsumption();
  const updateC = useUpdateConsumption();
  const deleteC = useDeleteConsumption();
  const submit = useSubmit();
  const { palette } = useTheme();

  const [foodId, setFoodId] = useState('');
  const [grams, setGrams] = useState('');
  const [meal, setMeal] = useState<Meal>('lunch');
  const [when, setWhen] = useState(nowLocalInputValue());
  const [period, setPeriod] = useState<Period>('30d');
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [editId, setEditId] = useState<string | null>(null);
  const [editGrams, setEditGrams] = useState('');
  const [editMeal, setEditMeal] = useState<Meal>('lunch');
  // Tableau des consommations : n'afficher qu'aujourd'hui (activé par défaut).
  const [todayOnly, setTodayOnly] = useState(true);

  const scoped = useMemo(
    () => filterByPeriod(consumptions, (c) => c.datetime, period),
    [consumptions, period],
  );

  const threshold = profile?.profile.calorieThreshold ?? 2000;
  const phase = profile?.profile.trainingPhase ?? 'cut';

  const tdeeKcal = useMemo(() => {
    if (!profile) return threshold;
    const t = currentTdee(profile.profile, weights);
    return Number.isFinite(t) ? t : threshold;
  }, [profile, weights, threshold]);

  const stats = useMemo(() => {
    const daily = dailyCalories(scoped, threshold, phase);
    const avg7 = movingAverage(
      daily.map((d) => d.kcal),
      7,
    );
    const chart = daily.map((d, i) => ({ date: d.date, kcal: d.kcal, avg7: round(avg7[i]!) }));
    const adh = adherence(scoped, threshold, phase);
    const balance = energyBalance(
      daily.filter((d) => d.kcal > 0).map((d) => d.kcal),
      tdeeKcal,
    );
    // Total du jour sur toutes les données (hors filtre période).
    const today =
      dailyCalories(consumptions, threshold, phase).find((d) => d.date === todayKey())?.kcal ?? 0;
    return { chart, adh, balance, today, todayVs: round(today - threshold) };
  }, [scoped, consumptions, threshold, phase, tdeeKcal]);

  const bucketed = useMemo(
    () => (granularity === 'day' ? [] : caloriesByGranularity(scoped, granularity)),
    [scoped, granularity],
  );

  const mealChart = useMemo(
    () =>
      aggregateBy(
        scoped,
        (c) => c.datetime,
        'day',
        (group) => {
          const per: Record<Meal, number> = { breakfast: 0, lunch: 0, dinner: 0, snack: 0 };
          for (const c of group) per[c.meal ?? 'snack'] += consumptionNutrients(c).kcal;
          return per;
        },
      ).map((b) => ({
        date: b.start,
        breakfast: round(b.value.breakfast),
        lunch: round(b.value.lunch),
        dinner: round(b.value.dinner),
        snack: round(b.value.snack),
      })),
    [scoped],
  );

  const heatmap = useMemo(
    () =>
      toCalendarMatrix(
        dailyCalories(consumptions, threshold, phase).map((d) => ({ date: d.date, value: d.kcal })),
        new Date(),
        13,
      ),
    [consumptions, threshold, phase],
  );

  // Consommations affichées dans les tableaux : toutes, ou seulement aujourd'hui.
  const tableEntries = useMemo(() => {
    const scopedByDay = todayOnly
      ? consumptions.filter((c) => dayKey(c.datetime) === todayKey())
      : consumptions;
    return scopedByDay
      .map((c) => ({ ...c, kcal: consumptionNutrients(c).kcal }))
      .sort((a, b) => b.datetime.localeCompare(a.datetime));
  }, [consumptions, todayOnly]);

  // Un groupe par type de repas (4 tableaux côte à côte).
  const entriesByMeal = useMemo(
    () =>
      MEAL_ORDER.map((m) => ({
        meal: m,
        entries: tableEntries.filter((c) => (c.meal ?? 'snack') === m),
      })),
    [tableEntries],
  );

  const favorites = useMemo(
    () =>
      frequentFoods(
        consumptions.map((c) => ({ foodId: c.foodId, foodName: c.foodName, grams: c.grams })),
      ),
    [consumptions],
  );

  function pickFavorite(foodId: string, grams: number) {
    setFoodId(foodId);
    setGrams(String(grams));
  }

  function quickLog(foodId: string, grams: number) {
    void submit.run(async () => {
      await createC.mutateAsync({ food: foodId, grams, datetime: new Date().toISOString(), meal });
    });
  }

  function addConsumption(e: FormEvent) {
    e.preventDefault();
    const g = Number(grams);
    if (!foodId || !grams || !Number.isFinite(g) || g <= 0) return;
    void submit.run(async () => {
      await createC.mutateAsync({
        food: foodId,
        grams: g,
        datetime: new Date(when).toISOString(),
        meal,
      });
      setGrams('');
      // Réhorodate à maintenant (figé au montage, sinon décalé après quelques minutes).
      setWhen(nowLocalInputValue());
    });
  }

  return (
    <>
      <div className="page-header">
        <div>
          <h1>
            Calories <PhaseBadge phase={phase} />
          </h1>
          <p>
            Seuil quotidien : <strong>{threshold} kcal</strong> · maintenance ≈{' '}
            {Math.round(tdeeKcal)} kcal
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <PeriodPicker value={period} onChange={setPeriod} />
          <GranularityPicker value={granularity} onChange={setGranularity} />
        </div>
      </div>

      {favorites.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>Saisie éclair</h3>
          <p className="muted" style={{ fontSize: '0.8rem', marginBottom: '0.6rem' }}>
            Tes aliments fréquents (clic = ajout direct au {MEAL_LABELS[meal].toLowerCase()} ·
            portion médiane) ; ✎ pré-remplit le formulaire.
          </p>
          <div className="toolbar" style={{ margin: 0, flexWrap: 'wrap' }}>
            {favorites.map((f) => (
              <span key={f.foodId} style={{ display: 'inline-flex', gap: 0 }}>
                <button
                  type="button"
                  disabled={submit.pending}
                  onClick={() => quickLog(f.foodId, f.medianGrams)}
                  title={`Ajouter ${f.medianGrams} g de ${f.foodName}`}
                >
                  {f.foodName} · {f.medianGrams} g
                </button>
                <button
                  type="button"
                  className="ghost"
                  aria-label={`Pré-remplir ${f.foodName}`}
                  onClick={() => pickFavorite(f.foodId, f.medianGrams)}
                  style={{ padding: '0 0.5rem' }}
                >
                  ✎
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      <form className="card toolbar" onSubmit={addConsumption} style={{ marginBottom: '1rem' }}>
        <div style={{ flex: '2 1 200px' }}>
          <label htmlFor="food">Aliment</label>
          <FoodPicker id="food" value={foodId} onChange={setFoodId} required />
        </div>
        <div>
          <label htmlFor="grams">Quantité (g)</label>
          <input
            id="grams"
            type="number"
            min={0}
            max={10000}
            value={grams}
            onChange={(e) => setGrams(e.target.value)}
            placeholder="150"
            style={{ maxWidth: 110 }}
            required
          />
        </div>
        <div>
          <label htmlFor="meal">Repas</label>
          <select id="meal" value={meal} onChange={(e) => setMeal(e.target.value as Meal)}>
            {MEAL_ORDER.map((m) => (
              <option key={m} value={m}>
                {MEAL_LABELS[m]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="when">Quand</label>
          <input
            id="when"
            type="datetime-local"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />
        </div>
        <button type="submit" disabled={submit.pending}>
          Ajouter
        </button>
      </form>
      {submit.error && <ErrorBanner message={submit.error} onRetry={submit.reset} />}

      {consumptions.length === 0 ? (
        <EmptyState>Ajoute ta première consommation pour voir les statistiques.</EmptyState>
      ) : (
        <>
          <div className="grid kpis" style={{ marginBottom: '1rem' }}>
            <Kpi
              label="Aujourd'hui"
              value={Math.round(stats.today)}
              unit="kcal"
              // Sèche : sous le seuil = bien. Prise de masse : au-dessus = bien.
              // Récup : viser le seuil, aucune direction « bonne » → delta neutre.
              delta={{
                value: stats.todayVs,
                unit: ' vs seuil',
                goodWhenNegative: phase === 'cut',
                neutral: phase === 'recovery',
              }}
            />
            <Kpi
              label="Moyenne (jours saisis)"
              value={stats.adh.avgKcal}
              unit="kcal"
              hint={`± ${stats.adh.stddevKcal} kcal`}
            />
            <Kpi
              label={ADHERENCE_LABEL[phase]}
              value={stats.adh.adherencePct}
              unit="%"
              hint={`${stats.adh.daysOnTarget}/${stats.adh.daysLogged} jours conformes`}
            />
            <Kpi
              label="Série en cours"
              value={stats.adh.currentStreak}
              unit="j"
              hint={`record : ${stats.adh.bestStreak} j`}
            />
            {phase === 'bulk' ? (
              <Kpi
                label="Surplus cumulé"
                value={-stats.balance.cumulativeDeficitKcal}
                unit="kcal"
                hint="apport au-dessus de la maintenance"
              />
            ) : phase === 'recovery' ? (
              <Kpi
                label="Bilan énergétique net"
                value={-stats.balance.cumulativeDeficitKcal}
                unit="kcal"
                hint="proche de 0 = maintenance"
              />
            ) : (
              <>
                <Kpi
                  label="Déficit cumulé"
                  value={stats.balance.cumulativeDeficitKcal}
                  unit="kcal"
                />
                <Kpi
                  label="Perte théorique"
                  value={stats.balance.predictedLossKg}
                  unit="kg"
                  hint="d'après le déficit énergétique"
                />
              </>
            )}
          </div>

          <div className="grid charts">
            {granularity === 'day' ? (
              <ChartCard
                title="Calories par jour"
                subtitle={`${CHART_LEGEND[phase]} · courbe = moyenne 7j`}
              >
                <CalorieChart
                  data={stats.chart}
                  threshold={threshold}
                  phase={phase}
                  palette={palette}
                />
              </ChartCard>
            ) : (
              <ChartCard
                title={`Calories par ${granularity === 'week' ? 'semaine' : 'mois'}`}
                subtitle="total des apports par période (le seuil est journalier)"
              >
                <BucketedCalorieChart data={bucketed} palette={palette} />
              </ChartCard>
            )}
            <ChartCard
              title="Répartition par repas"
              subtitle="calories empilées par moment de la journée"
            >
              <MealBarChart data={mealChart} palette={palette} />
            </ChartCard>
          </div>

          <div style={{ marginTop: '1rem' }}>
            <ChartCard
              title="Calendrier d'adhérence"
              subtitle={`13 dernières semaines · ${HEATMAP_LEGEND[phase]}`}
            >
              <CalendarHeatmap
                matrix={heatmap}
                threshold={threshold}
                phase={phase}
                palette={palette}
              />
            </ChartCard>
          </div>

          <div className="card" style={{ marginTop: '1rem' }}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.75rem',
                flexWrap: 'wrap',
              }}
            >
              <h3 style={{ margin: 0 }}>
                {todayOnly ? 'Consommations du jour' : 'Toutes les consommations'}
              </h3>
              <button
                type="button"
                className={todayOnly ? '' : 'ghost'}
                aria-pressed={todayOnly}
                style={{ padding: '0.35rem 0.8rem', fontSize: '0.82rem' }}
                onClick={() => setTodayOnly((v) => !v)}
              >
                {todayOnly ? "Aujourd'hui" : 'Tout'}
              </button>
            </div>
            {tableEntries.length === 0 ? (
              <p className="muted" style={{ fontSize: '0.85rem', marginTop: '0.5rem' }}>
                {todayOnly ? "Rien de saisi aujourd'hui." : 'Aucune consommation saisie.'}
              </p>
            ) : (
              <div className="grid meal-tables" style={{ marginTop: '0.75rem' }}>
                {entriesByMeal.map(({ meal: m, entries }) => {
                  const total = entries.reduce((sum, c) => sum + c.kcal, 0);
                  return (
                    <div key={m}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'baseline',
                          marginBottom: '0.3rem',
                        }}
                      >
                        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          {MEAL_LABELS[m]}
                        </span>
                        <span className="muted" style={{ fontSize: '0.8rem' }}>
                          {Math.round(total)} kcal
                        </span>
                      </div>
                      {entries.length === 0 ? (
                        <p className="muted" style={{ fontSize: '0.8rem', margin: 0 }}>
                          —
                        </p>
                      ) : (
                        <div className="table-wrap">
                          <table>
                            <thead>
                              <tr>
                                <th>{todayOnly ? 'Heure' : 'Date'}</th>
                                <th>Aliment</th>
                                <th>Qté</th>
                                <th>kcal</th>
                                <th></th>
                              </tr>
                            </thead>
                            <tbody>
                              {entries.map((c) => {
                                const editing = editId === c.id;
                                return (
                                  <tr key={c.id}>
                                    <td>
                                      {format(
                                        parseISO(c.datetime),
                                        todayOnly ? 'HH:mm' : 'dd/MM HH:mm',
                                        { locale: fr },
                                      )}
                                    </td>
                                    <td>{c.foodName || '—'}</td>
                                    <td>
                                      {editing ? (
                                        <input
                                          type="number"
                                          min={0}
                                          value={editGrams}
                                          onChange={(e) => setEditGrams(e.target.value)}
                                          style={{ maxWidth: 64 }}
                                        />
                                      ) : (
                                        `${c.grams} g`
                                      )}
                                    </td>
                                    <td>{Math.round(c.kcal)}</td>
                                    <td
                                      style={{
                                        display: 'flex',
                                        gap: '0.35rem',
                                        justifyContent: 'flex-end',
                                      }}
                                    >
                                      {editing ? (
                                        <>
                                          <button
                                            type="button"
                                            style={{
                                              padding: '0.3rem 0.55rem',
                                              fontSize: '0.8rem',
                                            }}
                                            onClick={() => {
                                              const g = Number(editGrams);
                                              if (!Number.isFinite(g) || g <= 0) return;
                                              void submit.run(async () => {
                                                await updateC.mutateAsync({
                                                  id: c.id,
                                                  grams: g,
                                                  meal: editMeal,
                                                });
                                                setEditId(null);
                                              });
                                            }}
                                          >
                                            OK
                                          </button>
                                          <button
                                            type="button"
                                            className="ghost"
                                            aria-label="Annuler la modification"
                                            style={{
                                              padding: '0.3rem 0.55rem',
                                              fontSize: '0.8rem',
                                            }}
                                            onClick={() => setEditId(null)}
                                          >
                                            ×
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <button
                                            type="button"
                                            className="ghost"
                                            aria-label={`Modifier ${c.foodName || 'la consommation'}`}
                                            style={{
                                              padding: '0.3rem 0.55rem',
                                              fontSize: '0.8rem',
                                            }}
                                            onClick={() => {
                                              setEditId(c.id);
                                              setEditGrams(String(c.grams));
                                              setEditMeal(c.meal ?? m);
                                            }}
                                          >
                                            ✎
                                          </button>
                                          <button
                                            type="button"
                                            className="danger"
                                            aria-label={`Supprimer ${c.foodName || 'la consommation'}`}
                                            style={{
                                              padding: '0.3rem 0.55rem',
                                              fontSize: '0.8rem',
                                            }}
                                            onClick={() => {
                                              if (confirmDelete('cette consommation'))
                                                deleteC.mutate(c.id);
                                            }}
                                          >
                                            ×
                                          </button>
                                        </>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
